import React, { useEffect, useState } from 'react'
import { Route, Switch, useHistory } from 'react-router-dom'
import {
  Box,
  Breadcrumbs,
  CssBaseline,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Theme,
  ThemeProvider,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import CookieIcon from '@mui/icons-material/Cookie'

import EventComponent from './Events/EventComponent'
import HeaderComponent from '../../shared/components/HeaderComponent'
import Gallery from './Gallery/Gallery'
import LoginComponent from './Login/LoginComponent'
import {
  Entity,
  FetchResponse,
  Message,
  Method,
  PermissionType,
  User,
  UserPermissions,
  UserRole,
} from '../../shared/types'
import Env from '../../shared/config/envConfig'
import fetchData from '../../fetch/fetchData'
import { getApiVersion } from '../../shared/config/getApiVersion'
import { useDispatch, useSelector } from 'react-redux'
import * as dataActions from '../../store/data/data.actions'
import { setStatistics } from '../../store/data/data.actions'
import * as settingsActions from '../../store/settings/settings.actions'
import { setUser } from '../../store/settings/settings.actions'
import { getNavPoint, getPrefersDarkMode, getRedirectPath, getUser } from '../../store/settings/settings.selectors'
import FamilyTree from '../../screens/App/FamilyMember/FamilyTree/FamilyTree'
import DashboardComponent from '../../screens/App/Dashboard/DashboardComponent'
import { useTranslation } from 'react-i18next'
import FooterComponent from '../../shared/components/FooterComponent'
import Imprint from '../../screens/App/Imprint/Imprint'
import Security from './Security/Security'
import Biography from './Dashboard/Biography'
import Geography from '../../screens/App/Dashboard/Geography'
import ButtonComponent from '../../shared/components/ButtonComponent'
import CheckBoxComponent from '../../shared/components/CheckBoxComponent'
import InfoIcon from '@mui/icons-material/Info'
import UserProfile from '../../screens/App/UserProfile/UserProfile'
import FamilyMember from './FamilyMember/FamilyMember'
import FamilyMemberTable from '../../screens/App/FamilyMember/FamilyMemberTable'
import FamilyCalendar from './FamilyMember/FamilyCalendar/FamilyCalendar'
import FamilyMap from './FamilyMember/FamilyMap'
import Statistics from '../../screens/App/Statistics/Statistics'
import { getAppTheme } from '../../shared/config/getAppTheme'
import SnackbarComponent from '../../shared/components/MessageComponents/SnackbarComponent'
import SearchResults from './SearchResults/SearchResults'
import LocationComponent from './Locations/LocationComponent'
import ForgotPassword from './UserProfile/ForgotPassword'
import { addCsrfHeaders } from '../../shared/config/csrf'

export enum MenuItem {
  family = 'family',
  events = 'events',
  gallery = 'gallery',
  locations = 'locations',
  familyMember = 'familyMember',
  statistics = 'statistics',
  imprint = 'imprint',
  security = 'security',
  userProfile = 'userProfile',
  searchResult = 'searchResult'
}

export interface AppSetting {
  title: string
  path: string
  value: string
}

const App: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()

  const user: User | undefined = useSelector(getUser)
  const darkMode: boolean = useSelector(getPrefersDarkMode)
  const navPoint: string[] = useSelector(getNavPoint)
  const redirectPath = useSelector(getRedirectPath)

  const [theme, setTheme] = useState<Theme>(getAppTheme(darkMode))

  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const basePath = Env.restBase
  const fetchHeaders = Env.fetchHeaders
  addCsrfHeaders(fetchHeaders)

  const [token, setToken] = useState<string | null>(null)
  const [showCookieDialog, setShowCookieDialog] = useState<boolean>(false)
  const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false)

  /* const importRelations = () => {
    const mockData = getMockDataRelations()
    const array = []
    mockData.results.map(mock => {
      // children
      mock.children.map(child => {
        const newRelation = {
          notes: undefined,
          relationFrom: undefined,
          relationTo: undefined,
          relationType: child.type,
          parent: mock.id,
          child: child.id,
        }
        // fetchData(basePath, getApiVersion(), fetchHeaders, Entity.relations, Method.POST, undefined, { relation: newRelation }, undefined, setMessage)
      })
      mock.parents.map(parent => {
        const newRelation = {
          notes: undefined,
          relationFrom: undefined,
          relationTo: undefined,
          relationType: parent.type,
          parent: parent.id,
          child: mock.id,
        }
        // fetchData(basePath, getApiVersion(), fetchHeaders, Entity.relations, Method.POST, undefined, { relation: newRelation }, undefined, setMessage)
      })
      mock.spouses.map(spouse => {
        const newRelation = {
          notes: undefined,
          relationFrom: undefined,
          relationTo: undefined,
          relationType: spouse.type,
          parent: mock.id,
          child: spouse.id,
        }
        if (array.findIndex(elem => (elem.parent === mock.id && elem.child === spouse.id) || (elem.parent === spouse.id && elem.child === mock.id)) === -1) {
          array.push(newRelation)
        }
      })
    })
    array.map(obj => {
      // fetchData(basePath, getApiVersion(), fetchHeaders, Entity.relations, Method.POST, undefined, { relation: obj }, undefined, setMessage)
    },
    )
  } */

  useEffect(() => {
    const restUrl = `${basePath}/api/session/user`
    fetch(restUrl,
      {
        method: 'GET',
        credentials: 'include',
        headers: fetchHeaders,
      })
      .then(res => res.json())
      .then(json => {
        !json.error && dispatch(setUser(json))
      })
      .catch(err => console.log(err))

    const pathname = window.location.pathname.split('/')
    if (pathname[1] === 'family') {
      if (pathname[2] === 'ottoMeissner') {
        dispatch(settingsActions.setBiography('ottoMeissner'))
      }
      if (pathname[2] === 'niederstaucha') {
        dispatch(settingsActions.setGeography('niederstaucha'))
      }
    }
    // importRelations()
  }, [],
  )

  useEffect(() => {
    setTheme(getAppTheme(darkMode))
  }, [darkMode],
  )

  useEffect(() => {
    const actualPath = history.location.pathname
    setToken(null)
    let pathname = '/'

    if (actualPath.search('forgotPassword') !== -1) {
      pathname = 'forgotPassword'
    } else {
      if (!user) {
        pathname = 'login'
      } else {
        if (redirectPath) {
          history.push(redirectPath)
        }
      }
    }

    switch (pathname) {
      case 'login':
        history.push('/login')
        break
      case 'forgotPassword':
        setToken(history.location.pathname.replace('forgotPassword/', '').replace('/', ''))
        break
      default:
        history.push('/family')
        break
    }
  }, [history, user],
  )

  const settings: AppSetting[] = [
    {
      title: t(`labels:${MenuItem.family}`),
      path: '/family',
      value: MenuItem.family,
    },
    {
      title: t(`labels:${MenuItem.familyMember}`),
      path: '/familyMember',
      value: MenuItem.familyMember,
    },
    {
      title: t(`labels:${MenuItem.events}`),
      path: '/events',
      value: MenuItem.events,
    },
  ]
  if (user?.userRole !== UserRole.user) {
    settings.push(
      {
        title: t(`labels:${MenuItem.locations}`),
        path: '/locations',
        value: MenuItem.locations,
      },
    )
  }
  settings.push(
    {
      title: t(`labels:${MenuItem.statistics}`),
      path: '/statistics',
      value: MenuItem.statistics,
    },
  )
  // TODO wenn Galerie fertig ist
  /*
  settings.push({
    title: t(`labels:${MenuItem.gallery}`),
    path: '/gallery',
    value: MenuItem.gallery,
  }) */

  const [message, setMessage] = useState<Message>()

  const setMemberData = (data: FetchResponse) => {
    const activeMember = data?.results.find(member => member.originId === user?.memberId)
    activeMember && dispatch(settingsActions.setActiveMember(activeMember))
    dispatch(dataActions.setMembers({ count: data.count, results: data.results }))
  }

  const setRelationData = (data: FetchResponse) => {
    dispatch(dataActions.setRelations({ count: data.count, results: data.results }))
  }

  const setEventData = (data: FetchResponse) => {
    dispatch(dataActions.setEvents({ count: data.count, results: data.results }))
  }

  const setEventMemberData = (data: FetchResponse) => {
    dispatch(dataActions.setEventMember({ count: data.count, results: data.results }))
  }

  const setEventPriceCategoryData = (data: FetchResponse) => {
    dispatch(dataActions.setEventPriceCategories({ count: data.count, results: data.results }))
  }

  const setEventLocations = (data: FetchResponse) => {
    dispatch(dataActions.setEventLocations({ count: data.count, results: data.results }))
  }

  useEffect(() => {
    message && dispatch(settingsActions.setMessage(message))
  }, [message])

  useEffect(() => {
    if (user) {
      const isAdmin = user?.userRole === UserRole.admin
      const isJury = user?.userRole === UserRole.jury

      const permissions: UserPermissions = {
        events: [PermissionType.read],
        relations: [PermissionType.read],
        members: [PermissionType.read],
        eventResults: [PermissionType.read],
        user: [],
        eventPriceCategories: [PermissionType.read],
        eventLocations: [PermissionType.read],
        eventMember: [PermissionType.read],
      }

      if (isJury) {
        permissions.eventResults.push(PermissionType.update)
        permissions.eventResults.push(PermissionType.delete)
        permissions.eventResults.push(PermissionType.create)
      }

      if (isAdmin) {
        permissions.events.push(PermissionType.update)
        permissions.events.push(PermissionType.delete)
        permissions.events.push(PermissionType.create)

        permissions.members.push(PermissionType.update)
        permissions.members.push(PermissionType.delete)
        permissions.members.push(PermissionType.create)

        permissions.relations.push(PermissionType.update)
        permissions.relations.push(PermissionType.delete)
        permissions.relations.push(PermissionType.create)

        permissions.eventPriceCategories.push(PermissionType.update)
        permissions.eventPriceCategories.push(PermissionType.delete)
        permissions.eventPriceCategories.push(PermissionType.create)

        permissions.eventLocations.push(PermissionType.update)
        permissions.eventLocations.push(PermissionType.delete)
        permissions.eventLocations.push(PermissionType.create)

        permissions.eventResults.push(PermissionType.update)
        permissions.eventResults.push(PermissionType.delete)
        permissions.eventResults.push(PermissionType.create)

        permissions.eventMember.push(PermissionType.update)
        permissions.eventMember.push(PermissionType.delete)
        permissions.eventMember.push(PermissionType.create)

        permissions.user.push(PermissionType.update)
        permissions.user.push(PermissionType.delete)
        permissions.user.push(PermissionType.create)
        permissions.user.push(PermissionType.read)
      }

      permissions.members.find(elem => elem === PermissionType.read) &&
      fetchData(basePath, getApiVersion(), fetchHeaders, Entity.members, Method.GET, undefined, undefined, setMemberData, setMessage)

      permissions.relations.find(elem => elem === PermissionType.read) &&
      fetchData(basePath, getApiVersion(), fetchHeaders, Entity.relations, Method.GET, undefined, undefined, setRelationData, setMessage)

      permissions.events.find(elem => elem === PermissionType.read) &&
      fetchData(basePath, getApiVersion(), fetchHeaders, Entity.events, Method.GET, undefined, undefined, setEventData, setMessage)

      permissions.eventMember.find(elem => elem === PermissionType.read) &&
      fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventMembers, Method.GET, undefined, undefined, setEventMemberData, setMessage)

      permissions.eventPriceCategories.find(elem => elem === PermissionType.read) &&
      fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventPriceCategories, Method.GET, undefined, undefined, setEventPriceCategoryData, setMessage)

      permissions.eventLocations.find(elem => elem === PermissionType.read) &&
      fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventLocations, Method.GET, undefined, undefined, setEventLocations, setMessage)

      dispatch(settingsActions.setUserPermissions(permissions))

      const restUrl = `${basePath}/api/${getApiVersion()}/statistics`
      fetch(restUrl,
        {
          method: 'GET',
          credentials: 'include',
          headers: fetchHeaders,
        })
        .then(res => res.json())
        .then(json => {
          !json.error && dispatch(setStatistics(json))
        })
        .catch(err => console.log(err))
    }
  }, [user])

  const LoginApp = () => (<LoginComponent />)
  const DashboardApp = () => (<DashboardComponent />)
  const EventApp = () => (<EventComponent />)
  const LocationApp = () => (<LocationComponent />)
  const GalleryApp = () => (<Gallery />)
  const FamilyTreeApp = () => (<FamilyTree />)
  const FamilyMapApp = () => (<FamilyMap />)
  const FamilyMemberApp = () => (<FamilyMember />)
  const FamilyMemberTableApp = () => (<FamilyMemberTable />)
  const FamilyCalendarApp = () => (<FamilyCalendar />)
  const StatisticApp = () => (<Statistics />)
  const BiographyApp = () => (<Biography />)
  const GeographyApp = () => (<Geography />)
  const SecurityApp = () => (<Security />)
  const ImprintApp = () => (<Imprint />)
  const UserProfileApp = () => (<UserProfile />)
  const SearchResultApp = () => (<SearchResults />)
  const ForgotPasswordApp = () => (<ForgotPassword />)

  const [breadcrumbs, setBreadcrumbs] = useState<JSX.Element[]>([])

  const onLogout = () => {
    const restUrl = `${basePath}/api/session/logout`
    fetch(restUrl,
      {
        method: 'POST',
        credentials: 'include',
        headers: fetchHeaders,
      })
      .then(() => dispatch(setUser(undefined)))
      .catch(err => console.log(err))
  }

  useEffect(() => {
    const array = [
      <Link
        sx={{ cursor: 'pointer' }}
        underline='hover'
        key='dashboard'
        color={theme.palette.primary.main}
        onClick={() => {
          history.replace('/')
          dispatch(settingsActions.setNavPoint([]))
        }}
      >
        Startseite
      </Link>,
    ]
    navPoint[0] && navPoint[1] && array.push(
      <Link
        sx={{ cursor: 'pointer' }}
        underline='hover'
        key={navPoint[0]}
        color={theme.palette.primary.main}
        onClick={() => {
          history.replace(`/${navPoint[0]}`)
          dispatch(settingsActions.setNavPoint([navPoint[0]]))
        }}
      >
        {t(`labels:${navPoint[0]}`)}
      </Link>)
    navPoint[0] && !navPoint[1] && array.push(
      <Typography key='3' color='text.primary' sx={{ fontSize: '16px' }}>
        {t(`labels:${navPoint[0]}`)}
      </Typography>)
    navPoint[1] && array.push(
      <Typography key='3' color='text.primary' sx={{ fontSize: '16px' }}>
        {t(`labels:${navPoint[1]}`)}
      </Typography>)
    setBreadcrumbs(array)
  },
  [navPoint],
  )

  const changeSearchInput = (value: string | undefined) => {
    const actualPath = history.location.pathname
    value && actualPath !== '/searchResults' && history.push('/searchResults')
    dispatch(settingsActions.setSearchInput(value))
    dispatch(settingsActions.setNavPoint([]))
  }

  return (
    <>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Switch>
          <Route path='/forgotPassword/:token?' component={ForgotPasswordApp} />
          <Route path='/login' component={LoginApp} />

          {user &&
            <>
              <HeaderComponent
                items={settings}
                onLogout={onLogout}
                changeSearch={(value?: string) => changeSearchInput(value,
                )}
              />
              <Box sx={{ height: '100%', width: '100%' }}>
                <Breadcrumbs
                  maxItems={downSm ? 2 : 10}
                  sx={{ fontSize: '16px', padding: downSm ? '30px 8px 0 8px' : '30px 32px 0 32px' }}
                  separator={<NavigateNextIcon fontSize='small' />}
                  aria-label='breadcrumb'
                >
                  {breadcrumbs}
                </Breadcrumbs>

                <Route exact path='/' component={DashboardApp} />
                <Route exact path='/family' component={DashboardApp} />
                <Route exact path='/family/niederstaucha' component={GeographyApp} />
                <Route exact path='/family/leipzig' component={GeographyApp} />
                <Route exact path='/family/ottoMeissner' component={BiographyApp} />

                <Route exact path='/gallery' component={GalleryApp} />
                <Route exact path='/events' component={EventApp} />
                <Route exact path='/locations' component={LocationApp} />

                <Route exact path='/statistics' component={StatisticApp} />
                <Route exact path='/familyMember' component={FamilyMemberApp} />
                <Route exact path='/familyMember/table' component={FamilyMemberTableApp} />
                <Route exact path='/familyMember/calendar' component={FamilyCalendarApp} />
                <Route exact path='/familyMember/tree' component={FamilyTreeApp} />
                <Route exact path='/familyMember/maAp' component={FamilyMapApp} />

                <Route exact path='/searchResults' component={SearchResultApp} />
                <Route exact path='/userProfile' component={UserProfileApp} />
                <Route exact path='/imprint' component={ImprintApp} />
                <Route exact path='/security' component={SecurityApp} />

                <SnackbarComponent />
                <Box
                  onClick={() => setShowCookieDialog(true)}
                  sx={{
                    position: 'fixed',
                    bottom: 20,
                    left: 20,
                    backgroundColor: theme.palette.primary.dark,
                    opacity: 0.8,
                    borderRadius: 50,
                    height: 50,
                    width: 50,
                    cursor: 'pointer',
                  }}
                >
                  <CookieIcon sx={{ color: '#fff', width: 30, height: 30, margin: '10px' }} />
                </Box>
                <Dialog
                  open={showCookieDialog}
                  onClose={() => {
                    setShowMoreInfo(false)
                    setShowCookieDialog(false)
                  }}
                  sx={{ '& .MuiDialog-paper': { borderRadius: 0, maxWidth: '80%' } }}
                >
                  <DialogTitle>
                    <Typography variant='h2'>Wir nutzen Cookies und andere Technologien.</Typography>
                  </DialogTitle>
                  <DialogContent>
                    <Typography variant='body1'>
                      Diese Website nutzt Cookies und vergleichbare Funktionen zur Verarbeitung von
                      Endgeräteinformationen
                      und personenbezogenen Daten. Die Verarbeitung dient der Einbindung von Inhalten,
                      externen Diensten
                      und Elementen Dritter, der statistischen Analyse/Messung, der personalisierten
                      Werbung oder des
                      Remarketings sowie der Einbindung sozialer Medien. Je nach Funktion werden dabei
                      Daten an Dritte
                      weitergegeben innerhalb der EU. Ihre Einwilligung ist stets freiwillig, für die
                      Nutzung unserer
                      Website nicht erforderlich und kann jederzeit über das Icon unten links
                      abgelehnt oder widerrufen
                      werden.
                    </Typography>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0' }}
                    >
                      <Box sx={{ width: '30%' }}>
                        <ButtonComponent
                          fullWidth size='large' text='Abbrechen'
                          onClick={() => {
                            setShowMoreInfo(false)
                            setShowCookieDialog(false)
                          }}
                        />
                      </Box>
                      <Box sx={{ width: '30%' }}>
                        <ButtonComponent
                          fullWidth size='large' text='Informationen'
                          onClick={() => setShowMoreInfo(!showMoreInfo)}
                        />
                      </Box>
                      <Box sx={{ width: '30%' }}>
                        <ButtonComponent
                          fullWidth size='large' text='Akzeptieren'
                          onClick={() => {
                            setShowMoreInfo(false)
                            setShowCookieDialog(false)
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: showMoreInfo ? 'block' : 'none' }}>
                      <Box sx={{ width: '100%', display: 'flex' }}>
                        <Stack marginTop='3px'>
                          <CheckBoxComponent value disabled />
                        </Stack>
                        <Stack>
                          <Box
                            sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                          >
                            <Typography variant='subtitle1'>Essentiell (3)</Typography>
                            <Tooltip title='hallo' placement='right'>
                              <IconButton><InfoIcon /></IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant='body1'>Hier sind alle technisch notwendigen
                            Skripte, Cookies und
                            sonstige Elemente zu finden die für den Betrieb der Webseite
                            notwendig sind oder der
                            Betreiber ein berechtiges Interesse nach DSGVO Art6, Abs. 1
                            hat.
                          </Typography>

                        </Stack>
                      </Box>
                      <Box sx={{ width: '100%', display: 'flex' }}>
                        <Stack marginTop='3px'>
                          <CheckBoxComponent value />
                        </Stack>
                        <Stack>
                          <Box
                            sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                          >
                            <Typography variant='subtitle1'>Funktionell (1)</Typography>
                            <Tooltip title='hallo' placement='right'>
                              <IconButton><InfoIcon /></IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant='body1'>Hier sind alle technisch notwendigen
                            Skripte, Cookies und
                            sonstige Elemente zu finden die für den Betrieb der Webseite
                            notwendig sind oder der
                            Betreiber ein berechtiges Interesse nach DSGVO Art6, Abs. 1
                            hat.
                          </Typography>

                        </Stack>
                      </Box>
                      <Box sx={{ width: '100%', display: 'flex' }}>
                        <Stack marginTop='3px'>
                          <CheckBoxComponent value />
                        </Stack>
                        <Stack>
                          <Box
                            sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                          >
                            <Typography variant='subtitle1'>Personalisierung (2)</Typography>
                            <Tooltip title='hallo' placement='right'>
                              <IconButton><InfoIcon /></IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant='body1'>Um die Webseite besser auf Sie abzustimmen
                            laufen hier Skripte und
                            es werden Cookies gesetzt. Ziel ist eine bessere Nutzererfahrung für
                            Sie.
                          </Typography>

                        </Stack>
                      </Box>
                    </Box>
                    <Typography variant='body1'>
                      <Link
                        sx={{ cursor: 'pointer' }}
                        underline='hover'
                        key='imprint'
                        color={theme.palette.primary.main}
                        onClick={() => {
                          setShowCookieDialog(false)
                          setShowMoreInfo(false)
                          history.replace('/imprint')
                          dispatch(settingsActions.setNavPoint(['imprint']))
                        }}
                      >
                        Impressum
                      </Link>&nbsp;&nbsp;&nbsp;&nbsp;<Link
                        sx={{ cursor: 'pointer' }}
                        underline='hover'
                        key='security'
                        color={theme.palette.primary.main}
                        onClick={() => {
                          setShowCookieDialog(false)
                          setShowMoreInfo(false)
                          history.replace('/security')
                          dispatch(settingsActions.setNavPoint(['security']))
                        }}
                      >
                        Datenschutzerklärung
                      </Link>
                    </Typography>
                  </DialogContent>
                </Dialog>
              </Box>
              <FooterComponent />
            </>}
        </Switch>
      </ThemeProvider>
    </>
  )
}

export default App
